import React, { useState, useEffect } from 'react';
import { 
  Trophy, 
  Calendar, 
  Clock,
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  ChevronRight,
  Send,
  FileText,
  BookOpen,
  User,
  ArrowLeft,
  Check,
  MessageSquare,
  Award
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Current student - Carlos Mendoza for demo
const CURRENT_STUDENT = {
  id: 's2a1',
  name: 'Carlos Mendoza',
  course: '2º de Bachiller',
  group: 'Grupo A'
};

// Format date to Spanish format
const formatDateSpanish = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  return `${parseInt(day)} de ${months[parseInt(month) - 1]} de ${year}`;
};

// Check if deadline has passed
const isDeadlinePassed = (deadline) => {
  if (!deadline) return false;
  const deadlineDate = new Date(deadline + 'T23:59:59');
  return new Date() > deadlineDate;
};

// Calculate days remaining until deadline
const getDaysRemaining = (deadline) => {
  if (!deadline) return null;
  const deadlineDate = new Date(deadline + 'T23:59:59');
  const now = new Date();
  const diffTime = deadlineDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// ============================================
// CHALLENGE CARD COMPONENT
// ============================================
const ChallengeCard = ({ challenge, onClick, onViewResults }) => {
  const deadline = challenge.deadline;
  const isPassed = isDeadlinePassed(deadline);
  const isSubmitted = challenge.submitted;
  const daysRemaining = getDaysRemaining(deadline);
  
  // Check if graded
  const isGraded = challenge.submission?.graded === true;
  const isPendingGrading = isSubmitted && !isGraded;
  
  // Determine if this is a "ready to answer" challenge (new or pending)
  const isReadyToAnswer = !isSubmitted && !isPassed;
  
  const handleClick = () => {
    if (isGraded && onViewResults) {
      onViewResults(challenge);
    } else if (isReadyToAnswer) {
      onClick(challenge);
    }
  };

  // Get status info
  const getStatusInfo = () => {
    if (isGraded) {
      return {
        label: 'Calificado',
        icon: CheckCircle2,
        bgClass: 'bg-blue-50',
        textClass: 'text-blue-600',
        iconClass: 'text-blue-500'
      };
    }
    if (isPendingGrading) {
      return {
        label: 'Entregado',
        icon: Clock,
        bgClass: 'bg-gray-100',
        textClass: 'text-gray-600',
        iconClass: 'text-gray-500'
      };
    }
    if (isPassed) {
      return {
        label: 'Expirado',
        icon: AlertCircle,
        bgClass: 'bg-red-50',
        textClass: 'text-red-600',
        iconClass: 'text-red-500'
      };
    }
    // Ready to answer - highlighted
    return {
      label: 'Listo',
      icon: FileText,
      bgClass: 'bg-emerald-50',
      textClass: 'text-emerald-600',
      iconClass: 'text-emerald-500',
      isHighlighted: true
    };
  };

  const status = getStatusInfo();
  const StatusIcon = status.icon;
  
  return (
    <button
      onClick={handleClick}
      disabled={isPassed && !isGraded}
      className={`w-full text-left p-4 rounded-2xl border transition-all ${
        isReadyToAnswer
          ? 'bg-white border-emerald-200 hover:border-emerald-300 hover:shadow-md cursor-pointer ring-1 ring-emerald-100'
          : isGraded
          ? 'bg-white border-gray-200 hover:border-gray-300 cursor-pointer'
          : isPendingGrading
          ? 'bg-white border-gray-200'
          : 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className={`p-1.5 rounded-lg ${
              isReadyToAnswer ? 'bg-emerald-50' : 'bg-gray-100'
            }`}>
              <Trophy className={`w-4 h-4 ${
                isReadyToAnswer ? 'text-emerald-500' : 'text-gray-400'
              }`} />
            </div>
            <h3 className={`text-sm font-semibold ${
              isReadyToAnswer ? 'text-gray-900' : 'text-gray-700'
            }`}>{challenge.topic}</h3>
          </div>
          
          {/* Days remaining - only for ready to answer challenges */}
          {isReadyToAnswer && daysRemaining !== null && (
            <div className="mb-2 ml-8">
              <span className={`text-xs font-medium ${
                daysRemaining <= 1 ? 'text-red-500' : 'text-gray-500'
              }`}>
                {daysRemaining === 0 ? '¡Último día!' :
                 daysRemaining === 1 ? '1 día restante' :
                 `${daysRemaining} días restantes`}
              </span>
            </div>
          )}
          
          {/* Deadline info */}
          <div className="flex items-center gap-2 ml-8">
            <Calendar className="w-3 h-3 text-gray-300" />
            <span className="text-xs text-gray-400">
              {formatDateSpanish(deadline)}
            </span>
          </div>
        </div>
        
        {/* Status badge */}
        <div className="flex-shrink-0">
          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full ${status.bgClass}`}>
            <StatusIcon className={`w-3.5 h-3.5 ${status.iconClass}`} />
            <span className={`text-xs font-medium ${status.textClass}`}>
              {status.label}
            </span>
          </div>
        </div>
      </div>
      
      {/* Action hint for ready to answer */}
      {isReadyToAnswer && (
        <div className="mt-3 pt-3 border-t border-emerald-100 flex items-center justify-center gap-1">
          <span className="text-xs text-emerald-600 font-medium">Responder reto</span>
          <ChevronRight className="w-3.5 h-3.5 text-emerald-500" />
        </div>
      )}
      
      {/* Status hint for other states */}
      {isGraded && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-center gap-1">
          <span className="text-xs text-gray-400">Ver detalles</span>
          <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
        </div>
      )}
      
      {isPendingGrading && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center">
            Pendiente de calificación por el profesor
          </p>
        </div>
      )}
    </button>
  );
};

// ============================================
// RESULTS VIEW (for viewing graded challenge feedback)
// ============================================
const ResultsView = ({ challenge, submission, onBack }) => {
  const feedback = submission.feedback;
  
  return (
    <div className="flex flex-col h-full p-6 pr-8 overflow-y-auto">
      {/* Header */}
      <div className="flex-shrink-0 mb-6 max-w-2xl">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a mis retos
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-xl">
            <Award className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-800">{challenge.topic}</h1>
            <p className="text-xs text-gray-500">Resultados de tu reto</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl space-y-4">
        {/* Score card */}
        <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
          <div className="text-center">
            <p className="text-4xl font-bold text-blue-500 mb-1">
              {submission.score}/{submission.total_questions}
            </p>
            <p className="text-sm text-gray-500">aciertos</p>
            <div className="flex items-center justify-center gap-2 mt-3">
              <div className="h-2 w-32 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 rounded-full transition-all"
                  style={{ width: `${submission.percentage}%` }}
                />
              </div>
              <span className="text-lg font-semibold text-blue-600">{submission.percentage}%</span>
            </div>
          </div>
        </div>

        {/* Feedback card */}
        {feedback && (
          <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">{feedback.emoji || '📝'}</span>
              <p className="text-sm font-medium text-gray-700">{feedback.resumen}</p>
            </div>

            {feedback.temas_dominados && feedback.temas_dominados.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-emerald-600 mb-2 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Temas que dominas
                </p>
                <div className="flex flex-wrap gap-2">
                  {feedback.temas_dominados.map((tema, i) => (
                    <span key={i} className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-lg">
                      {tema}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {feedback.temas_reforzar && feedback.temas_reforzar.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-amber-600 mb-2 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Temas a reforzar
                </p>
                <div className="flex flex-wrap gap-2">
                  {feedback.temas_reforzar.map((tema, i) => (
                    <span key={i} className="px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded-lg">
                      {tema}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {feedback.recomendaciones && feedback.recomendaciones.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">💡 Recomendaciones de estudio</p>
                <ul className="space-y-2">
                  {feedback.recomendaciones.map((rec, i) => (
                    <li key={i} className="text-xs text-gray-600 flex items-start gap-2 bg-gray-50 p-2 rounded-lg">
                      <span className="text-gray-400 mt-0.5">•</span>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* No feedback fallback */}
        {!feedback && (
          <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm text-center">
            <p className="text-sm text-gray-500">
              El feedback detallado no está disponible para este reto.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// CHALLENGE ANSWERING VIEW
// ============================================
const ChallengeAnswerView = ({ challenge, onBack, onSubmit }) => {
  const [answers, setAnswers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);

  const questions = challenge.questions || [];

  // Initialize answers array
  useEffect(() => {
    setAnswers(questions.map(q => ({
      type: q.type || 'multiple_choice',
      answer: q.type === 'open' ? '' : null
    })));
  }, [questions]);

  const handleSelectAnswer = (questionIndex, answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = { ...newAnswers[questionIndex], answer: answerIndex };
    setAnswers(newAnswers);
  };

  const handleOpenAnswer = (questionIndex, text) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = { ...newAnswers[questionIndex], answer: text };
    setAnswers(newAnswers);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/student/challenges/${challenge.id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: CURRENT_STUDENT.id,
          student_name: CURRENT_STUDENT.name,
          answers: answers
        })
      });

      if (!response.ok) {
        throw new Error('Error al enviar el reto');
      }

      const data = await response.json();
      setResults(data);
      setShowResults(true);
    } catch (err) {
      console.error('Error submitting challenge:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const answeredCount = answers.filter(a => 
    a.type === 'open' ? (a.answer && a.answer.trim().length > 0) : a.answer !== null
  ).length;

  const canSubmit = answeredCount === questions.length;

  // Results view - shows "Pending grading" or actual score with feedback
  if (showResults && results) {
    const isPendingGrading = results.status === 'pending_grading' || !results.percentage;
    
    return (
      <div className="flex flex-col h-full p-6 overflow-hidden overflow-y-auto">
        <div className="flex-1 flex flex-col items-center">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
            isPendingGrading ? 'bg-gray-100' : 'bg-blue-50'
          }`}>
            {isPendingGrading ? (
              <Clock className="w-10 h-10 text-gray-400" />
            ) : (
              <Award className="w-10 h-10 text-blue-500" />
            )}
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-1">
            {isPendingGrading ? '¡Reto entregado!' : '¡Reto calificado!'}
          </h2>
          <p className="text-gray-500 text-sm mb-6">
            {isPendingGrading 
              ? 'Tus respuestas han sido enviadas correctamente'
              : 'Tu reto ha sido evaluado'}
          </p>
          
          {isPendingGrading ? (
            <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm text-center max-w-md">
              <div className="flex items-center justify-center gap-2 text-gray-500 mb-3">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-medium">Pendiente de calificación</span>
              </div>
              <p className="text-xs text-gray-400">
                Tu profesor calificará este reto pronto. Podrás ver tu puntuación y feedback cuando esté disponible.
              </p>
            </div>
          ) : (
            <div className="w-full max-w-lg space-y-4">
              {/* Score card */}
              <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="text-center mb-4">
                  <p className="text-4xl font-bold text-blue-500 mb-1">
                    {results.score}/{results.total_questions || results.total}
                  </p>
                  <p className="text-sm text-gray-500">aciertos</p>
                  <div className="flex items-center justify-center gap-2 mt-3">
                    <div className="h-2 w-32 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full transition-all"
                        style={{ width: `${results.percentage}%` }}
                      />
                    </div>
                    <span className="text-lg font-semibold text-blue-600">{results.percentage}%</span>
                  </div>
                </div>
              </div>

              {/* Feedback card - only show if we have feedback from the graded challenge */}
              {results.feedback && (
                <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">{results.feedback.emoji || '📝'}</span>
                    <p className="text-sm font-medium text-gray-700">{results.feedback.resumen}</p>
                  </div>

                  {results.feedback.temas_dominados && results.feedback.temas_dominados.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-emerald-600 mb-2 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Temas que dominas
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {results.feedback.temas_dominados.map((tema, i) => (
                          <span key={i} className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-lg">
                            {tema}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {results.feedback.temas_reforzar && results.feedback.temas_reforzar.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-amber-600 mb-2 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Temas a reforzar
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {results.feedback.temas_reforzar.map((tema, i) => (
                          <span key={i} className="px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded-lg">
                            {tema}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {results.feedback.recomendaciones && results.feedback.recomendaciones.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-2">💡 Recomendaciones</p>
                      <ul className="space-y-1">
                        {results.feedback.recomendaciones.map((rec, i) => (
                          <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                            <span className="text-gray-400 mt-0.5">•</span>
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <button
            onClick={onBack}
            className="mt-6 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
          >
            Volver a mis retos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-4 pr-6 overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 mb-4 max-w-2xl">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a mis retos
        </button>
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 bg-gray-100 rounded-xl">
            <Trophy className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-800">{challenge.topic}</h1>
            <p className="text-xs text-gray-500">
              Fecha límite: {formatDateSpanish(challenge.deadline)}
            </p>
          </div>
        </div>
      </div>

      {/* Progress bar - minimalist gray */}
      <div className="flex-shrink-0 mb-5 p-3 bg-white rounded-xl border border-gray-100 max-w-2xl">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">Progreso</span>
          <span className={`text-xs font-semibold ${answeredCount === questions.length ? 'text-blue-500' : 'text-gray-600'}`}>
            {answeredCount}/{questions.length} respondidas
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-300 ${
              answeredCount === questions.length ? 'bg-blue-500' : 'bg-gray-400'
            }`}
            style={{ width: `${(answeredCount / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Questions - minimalist with salmon only for selection */}
      <div className="flex-1 overflow-y-auto space-y-5 pr-4">
        {questions.map((question, qIdx) => {
          const isOpen = question.type === 'open';
          const currentAnswer = answers[qIdx];
          const isAnswered = isOpen 
            ? (currentAnswer?.answer && currentAnswer.answer.trim().length > 0) 
            : currentAnswer?.answer !== null;
          
          return (
            <div 
              key={qIdx} 
              className={`p-4 bg-white rounded-2xl border transition-all max-w-2xl ${
                isAnswered ? 'border-blue-200' : 'border-gray-200'
              }`}
            >
              {/* Question header - gray numbers */}
              <div className="flex items-start gap-3 mb-4">
                <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  isAnswered 
                    ? 'bg-blue-50 text-blue-500' 
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {isAnswered ? <Check className="w-4 h-4" /> : qIdx + 1}
                </span>
                <div className="flex-1">
                  <p className="text-sm text-gray-800 font-medium leading-relaxed">{question.question}</p>
                </div>
              </div>

              {/* Answer options - SALMON only for selected state */}
              {isOpen ? (
                <div className="ml-11">
                  <textarea
                    value={currentAnswer?.answer || ''}
                    onChange={(e) => handleOpenAnswer(qIdx, e.target.value)}
                    placeholder="Escribe tu respuesta aquí (máximo 150 palabras)"
                    maxLength={1000}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#E5989B] focus:ring-2 focus:ring-[#E5989B]/20 resize-none transition-all"
                    rows={4}
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">
                    {(currentAnswer?.answer || '').split(/\s+/).filter(w => w).length}/150 palabras
                  </p>
                </div>
              ) : (
                <div className="ml-11 space-y-2">
                  {question.options?.map((opt, optIdx) => {
                    const isSelected = currentAnswer?.answer === optIdx;
                    return (
                      <button
                        key={optIdx}
                        onClick={() => handleSelectAnswer(qIdx, optIdx)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-left transition-all ${
                          isSelected
                            ? 'bg-[#E5989B]/10 border-2 border-[#E5989B] text-gray-800'
                            : 'bg-gray-50 border-2 border-transparent text-gray-600 hover:bg-gray-100 hover:border-gray-300'
                        }`}
                      >
                        <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-medium transition-all ${
                          isSelected
                            ? 'border-[#E5989B] bg-[#E5989B] text-white'
                            : 'border-gray-300 text-gray-400'
                        }`}>
                          {isSelected ? <Check className="w-3 h-3" /> : String.fromCharCode(65 + optIdx)}
                        </span>
                        <span className="flex-1">{opt}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Submit section - reduced width */}
        <div className="mt-5 mb-4 p-4 bg-white rounded-2xl border border-gray-100 max-w-2xl">
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${canSubmit ? 'text-blue-500' : 'text-gray-600'}`}>
                {canSubmit ? '¡Listo para enviar!' : `Faltan ${questions.length - answeredCount} preguntas`}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Una vez enviado, no podrás modificar tus respuestas
              </p>
            </div>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all shadow-md shadow-blue-200 disabled:shadow-none"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Enviar respuestas
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const StudentRetosView = ({ openChallengeId, onChallengeOpened }) => {
  const [challenges, setChallenges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [error, setError] = useState('');

  // Fetch challenges for current student
  useEffect(() => {
    fetchChallenges();
  }, []);

  // Handle opening a specific challenge from notification
  useEffect(() => {
    if (openChallengeId && challenges.length > 0 && !isLoading) {
      const challengeToOpen = challenges.find(c => c.id === openChallengeId);
      if (challengeToOpen && !challengeToOpen.submitted && !isDeadlinePassed(challengeToOpen.deadline)) {
        setSelectedChallenge(challengeToOpen);
      }
      // Clear the openChallengeId after processing
      if (onChallengeOpened) {
        onChallengeOpened();
      }
    }
  }, [openChallengeId, challenges, isLoading, onChallengeOpened]);

  const fetchChallenges = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/student/challenges/${CURRENT_STUDENT.id}`);
      if (!response.ok) {
        throw new Error('Error al cargar retos');
      }
      const data = await response.json();
      setChallenges(data);
    } catch (err) {
      console.error('Error fetching challenges:', err);
      setError('No se pudieron cargar los retos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectChallenge = (challenge) => {
    setSelectedChallenge(challenge);
  };

  const handleViewResults = (challenge) => {
    // Set selected challenge with results info for viewing feedback
    setSelectedChallenge({
      ...challenge,
      viewingResults: true
    });
  };

  const handleBack = () => {
    setSelectedChallenge(null);
    fetchChallenges();
  };

  // Show challenge answering view or results view
  if (selectedChallenge) {
    // If viewing results of a graded challenge
    if (selectedChallenge.viewingResults && selectedChallenge.submission) {
      return (
        <ResultsView 
          challenge={selectedChallenge}
          submission={selectedChallenge.submission}
          onBack={handleBack}
        />
      );
    }
    // Otherwise show the answering view
    return (
      <ChallengeAnswerView
        challenge={selectedChallenge}
        onBack={handleBack}
        onSubmit={() => {}}
      />
    );
  }

  // Separate pending and completed challenges
  const pendingChallenges = challenges.filter(c => !c.submitted && !isDeadlinePassed(c.deadline));
  const completedChallenges = challenges.filter(c => c.submitted);
  const expiredChallenges = challenges.filter(c => !c.submitted && isDeadlinePassed(c.deadline));

  return (
    <div className="flex flex-col h-full p-4 pr-6 overflow-hidden" data-testid="student-retos-view">
      {/* Header - minimalist gray */}
      <div className="flex-shrink-0 mb-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-gray-100 rounded-xl">
            <Trophy className="w-5 h-5 text-gray-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-800">Mis Retos</h1>
        </div>
        <p className="text-sm text-gray-500 ml-12">
          Bienvenido, <span className="font-medium text-gray-700">{CURRENT_STUDENT.name}</span> • {CURRENT_STUDENT.course}, {CURRENT_STUDENT.group}
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pr-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin mb-4" />
            <p className="text-sm text-gray-500">Cargando tus retos...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-red-50 rounded-full mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        ) : challenges.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-gray-100 rounded-full mb-4">
              <Trophy className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-600">No tienes retos asignados</p>
            <p className="text-xs text-gray-400 mt-1">Los retos aparecerán aquí cuando tu profesor los envíe</p>
          </div>
        ) : (
          <div className="space-y-6 max-w-2xl">
            {/* Pending Challenges - gray section header */}
            {pendingChallenges.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1 bg-gray-100 rounded-lg">
                    <Clock className="w-4 h-4 text-gray-500" />
                  </div>
                  <h2 className="text-sm font-semibold text-gray-700">
                    Pendientes
                  </h2>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                    {pendingChallenges.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {pendingChallenges.map(challenge => (
                    <ChallengeCard
                      key={challenge.id}
                      challenge={challenge}
                      onClick={handleSelectChallenge}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Completed Challenges */}
            {completedChallenges.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1 bg-blue-50 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                  </div>
                  <h2 className="text-sm font-semibold text-gray-700">
                    Completados
                  </h2>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full">
                    {completedChallenges.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {completedChallenges.map(challenge => (
                    <ChallengeCard
                      key={challenge.id}
                      challenge={challenge}
                      onClick={() => {}}
                      onViewResults={handleViewResults}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Expired Challenges */}
            {expiredChallenges.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1 bg-red-50 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  </div>
                  <h2 className="text-sm font-semibold text-gray-700">
                    Expirados
                  </h2>
                  <span className="px-2 py-0.5 bg-red-50 text-red-500 text-xs font-medium rounded-full">
                    {expiredChallenges.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {expiredChallenges.map(challenge => (
                    <ChallengeCard
                      key={challenge.id}
                      challenge={challenge}
                      onClick={() => {}}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentRetosView;
